import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Badge, Button, Card, Empty, Field, Page, Table, TextInput } from '../components/ui.jsx';

interface Property {
  id: string;
  name: string;
  type: string | null;
  address: string | null;
  isActive: boolean;
  createdAt: number;
}

export function PropertiesPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['properties'],
    queryFn: () => api<{ properties: Property[] }>('/api/v1/properties'),
  });

  return (
    <Page
      title="Properties"
      actions={
        <Button onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : '+ New property'}
        </Button>
      }
    >
      {showForm && (
        <div className="mb-4">
          <PropertyForm
            onSaved={() => {
              setShowForm(false);
              void qc.invalidateQueries({ queryKey: ['properties'] });
            }}
          />
        </div>
      )}

      {isLoading ? (
        <Empty>Loading…</Empty>
      ) : !data?.properties.length ? (
        <Empty>No properties yet. Create your first one above.</Empty>
      ) : (
        <Table>
          <thead className="text-left text-slate-500">
            <tr>
              <th className="border-b border-slate-200 px-3 py-2 dark:border-slate-700">Name</th>
              <th className="border-b border-slate-200 px-3 py-2 dark:border-slate-700">Type</th>
              <th className="border-b border-slate-200 px-3 py-2 dark:border-slate-700">Address</th>
              <th className="border-b border-slate-200 px-3 py-2 dark:border-slate-700">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.properties.map((p) => (
              <tr key={p.id} className="border-b border-slate-100 dark:border-slate-800">
                <td className="px-3 py-2">
                  <Link to={`/properties/${p.id}`} className="font-medium underline decoration-dotted">
                    {p.name}
                  </Link>
                </td>
                <td className="px-3 py-2 text-slate-500">{p.type ?? '—'}</td>
                <td className="px-3 py-2 text-slate-500">{p.address ?? '—'}</td>
                <td className="px-3 py-2">
                  {p.isActive ? <Badge tone="good">Active</Badge> : <Badge>Inactive</Badge>}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Page>
  );
}

function PropertyForm({ onSaved }: { onSaved: () => void }) {
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [address, setAddress] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: { name: string; type?: string; address?: string }) =>
      api<{ property: Property }>('/api/v1/properties', { method: 'POST', body }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      name,
      type: type || undefined,
      address: address || undefined,
    });
  }

  return (
    <Card>
      <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
        <Field label="Name">
          <TextInput required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Green Villa" />
        </Field>
        <Field label="Type" hint="Free form: residential, commercial, mixed…">
          <TextInput value={type} onChange={(e) => setType(e.target.value)} />
        </Field>
        <Field label="Address" hint="Optional">
          <TextInput value={address} onChange={(e) => setAddress(e.target.value)} />
        </Field>
        <div className="md:col-span-2 flex items-center justify-end gap-2">
          {error && <span className="text-sm text-red-600">{error}</span>}
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Create property'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
